import { FC, ReactNode, useEffect, useState } from "react";
import { useGlobalMessageActions } from "../../../entities/globalMessage";
import { FEATURES, hasFeature, useMyActions } from "../../../entities/my";
import { MyError } from "../../../shared/lib/error/MyError";
import { AuthError } from "../../../shared/lib/error/AuthError";
import classes from './staffPenaltyBonus.module.scss'
import { OpenContainer } from "../../../features/openContainer";
import { getDateUTC } from "../../../shared/lib/helpers/getDateUTC";
import { Data } from "./data/Data";
import { IPenaltyBonusItem, IPenaltyBonusSummary, penaltyBonusService, TPenaltyBonus } from "../../../entities/penaltyBonus";
import { AddPenaltyBonus } from "../../../features/addPenaltyBonusTutor";
import { useAppSelector } from "../../../app/store/store";

interface IProps {
    userId: number;
    titleIcon?: ReactNode;
}

export const AssistantPenaltyBonus: FC<IProps> = ({userId, titleIcon}) => {

    const [isLoading, setIsLoading] = useState<boolean>(false)
    const {setGlobalMessage} = useGlobalMessageActions()
    const {setIsAuth} = useMyActions()
    const {my} = useAppSelector(s => s.myReducer)
    const targetUserId = userId
    const canRead = hasFeature(my, FEATURES.IDM.PENALTIES_BONUSES_READ, targetUserId)
    const canCreate = hasFeature(my, FEATURES.IDM.PENALTIES_BONUSES_CREATE, targetUserId)
    const canDelete = canCreate
    const [items, setItems] = useState<IPenaltyBonusItem[]>([])
    const [summary, setSummary] = useState<IPenaltyBonusSummary | null>(null)
    const [range, setRange] = useState<{from: string, to: string} | null>(null)
    const [trigger, setTrigger] = useState<number>(0)

    const getData = async (from: string, to: string) => {
        try{
            setIsLoading(true)
            const {summary, items} = await penaltyBonusService.userGetPenaltiesBonuses(userId, from, to)
            setSummary(summary)
            setItems(items)
        }
        catch(e){
            console.log(e)
            if(e instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else if(e instanceof MyError){
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else if(e instanceof MyError && (e.status === 404)){}
            else{
                setGlobalMessage({message: 'Ошибка при получении списка премирований и депремирований', type: 'error'})
            }
        }
        finally{
            setIsLoading(false)
        }
    }

    const setDate = (startDate: Date | null, endDate: Date | null) => {
        if(startDate && endDate){
            const nextRange = {from: getDateUTC(startDate), to: getDateUTC(endDate)}
            setRange(nextRange)
            getData(nextRange.from, nextRange.to)
        }
    }

    const addReq = (amount: string, comment: string, type: TPenaltyBonus, actualDate: string) => {
        return penaltyBonusService.userAddPenaltiesBonuses(userId, amount, comment, type, actualDate)
    }

    const deleteReq = async (item: IPenaltyBonusItem) => {
        await penaltyBonusService.userDeletePenaltiesBonuses(userId, +item.id)
        if(range){
            await getData(range.from, range.to)
            return
        }
        setItems(state => state.filter(currentItem => currentItem.id !== item.id))
    }

    useEffect(() => {
        setTrigger(prev => prev + 1)
    }, [isLoading, items, summary])

    if(!canRead && !canCreate){
        return null
    }

    return (
        <section className={classes.container}>
            <OpenContainer title="Премирование и депремирование" icon={titleIcon} trigger={trigger} allowOverflow>
                <section className={classes.content}>
                    {canCreate && <AddPenaltyBonus request={addReq} bonusLabel="Премирование" penaltyLabel="Депремирование" />}
                    {canCreate && canRead && <span className={classes.hr} />}
                    {canRead && <Data
                        setDate={setDate}
                        summary={summary}
                        items={items}
                        isLoading={isLoading}
                        onDelete={deleteReq}
                        canDelete={canDelete}
                    />}
                </section>
            </OpenContainer>
        </section>
    )
}
